/* eslint-disable jsx-a11y/anchor-is-valid */

import {
  bulkUpdateRows,
  getNumSelectedRowsInTable,
  getTableStateForThread,
  updateTableRow,
} from '@/store/reducers/TableWidgetStore';
import { ChatUIContext } from '@/components/context/ChatUIContext';
import {
  closeWidget,
  getCurrentOpenWidgetIds,
  getCurrentThread,
} from '@/store/reducers/UIState';
import { ColumnModal } from '@/components/table/ColumnModal';
import {
  editTableCell,
  removeTableRows,
  reorderTableColumns,
  reorderTableRows,
} from '@/store/thunks/UpdateTable';
import { EventType } from '@/models/Interaction';
import { getChatAPI } from '@/api/ChatApi';
import { getWidgetTypefromId } from '@/store/actionCreators/WidgetActions';
import { LoadingTable } from '@/components/table/LoadingTable';
import {
  mkCloseBtnTrackAttrs,
  mkHeaderDeleteSelectedBtnTrackAttrs,
} from '@/analytics/props/litTable';
import { optAppConfig } from '@/utils/config';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import {
  StyledTableRow,
  TableView,
  TableViewMode,
} from '@/components/table/TableView';
import { TABLE_ANNOTATION } from 'configs/NoraVariants';
import { TableControls } from '@/components/table/TableControls';
import { TableRemote } from '@/components/table/TableRemote';
import { TableRow } from '@/models/Table';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { WidgetEvent } from '@/models/PanelWidgets';

import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  styled,
  Typography,
} from '@mui/material';
import {
  ChangeEvent,
  Fragment,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

export enum DragType {
  ROW = 'row',
  COLUMN = 'column',
}

const defaultTopic = 'LLM evaluation';
const pre = 'Add a few papers on ';
const post =
  ' in a literature review table with 3 suggested aspects for comparing them.';
const suggestedQuery = `${pre}${defaultTopic}${post}`;

const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

export const Table = (): ReactNode => {
  const dispatch = useAppDispatch();
  const currentThread = useAppSelector(getCurrentThread);
  const { setSuggestedChatMessage } = useContext(ChatUIContext);
  const { isReadOnly } = useContext(PermissionsContext);
  const [isAddColumnModalOpen, setIsAddColumnModalOpen] = useState(false);

  const handleShowSuggestion: MouseEventHandler<HTMLAnchorElement> =
    useCallback(
      (event) => {
        event.preventDefault();
        setSuggestedChatMessage(
          suggestedQuery,
          pre.length,
          pre.length + defaultTopic.length,
        );
      },
      [setSuggestedChatMessage],
    );

  const tableData = useAppSelector((state) =>
    getTableStateForThread(state, currentThread?.id),
  );

  const numRowsSelected = useAppSelector((state) =>
    getNumSelectedRowsInTable(state, currentThread?.id),
  );

  const title = tableData?.title || 'Literature Review Table';
  const columns = tableData?.columns || [];
  const rows = tableData?.rows || [];
  const cells = tableData?.cells || {};

  const handleChangeRowSelection = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rowId = event.target.value;
      const row = rows.find((r) => r.id === rowId);

      if (!currentThread || !row) {
        console.log('No current thread or row found');
        return;
      }

      const updatedRow: TableRow = {
        ...row,
        isSelected: event.target.checked,
      };

      dispatch(
        updateTableRow({
          currentThreadId: currentThread.id,
          row: updatedRow,
        }),
      );
    },
    [dispatch, currentThread, rows],
  );

  const handleSelectAllRows = useCallback(() => {
    if (!currentThread) {
      return;
    }

    // if everything is selected, deselect all
    if (numRowsSelected === rows.length) {
      dispatch(
        bulkUpdateRows({
          currentThreadId: currentThread?.id,
          updates: { isSelected: false },
        }),
      );
      return;
    }

    // if nothing or indeterminate, select all rows
    dispatch(
      bulkUpdateRows({
        currentThreadId: currentThread?.id,
        updates: { isSelected: true },
      }),
    );
  }, [dispatch, rows, currentThread, numRowsSelected]);

  const removeSelectedRows = useCallback(() => {
    if (!currentThread) {
      return;
    }

    dispatch(
      removeTableRows({
        currentThreadId: currentThread.id,
        rowIds: rows.filter((row) => row.isSelected).map((row) => row.id),
      }),
    );
  }, [rows, dispatch, currentThread]);

  const isTableEmpty = rows.length === 0 && columns.length === 0;

  const chatApi = getChatAPI();
  const openWidgetIds = useAppSelector(getCurrentOpenWidgetIds);
  const closePanel = useCallback((): void => {
    dispatch(closeWidget());

    chatApi.trackEvent({
      type: EventType.UI_INTERACTION,
      threadId: currentThread?.id ?? undefined,
      data: {
        widget_type: getWidgetTypefromId(openWidgetIds[0]),
        event_type: WidgetEvent.WIDGET_CLOSE,
      },
    });
  }, [currentThread, openWidgetIds]);

  const openAddColumnModal = (): void => {
    setIsAddColumnModalOpen(true);
  };

  const handleEditTableCell = useCallback(
    (cellKey: string, editedValue: string) => {
      if (!currentThread) {
        return;
      }

      dispatch(
        editTableCell({
          currentThreadId: currentThread.id,
          cellKey: cellKey,
          editedValue: editedValue,
        }),
      );
    },
    [dispatch, currentThread],
  );

  const closeAddColumnModal = (): void => {
    setIsAddColumnModalOpen(false);
  };

  const onDragEnd = (e: DragEndEvent): void => {
    if (!currentThread || !e.over || e.active.id === e.over.id) {
      return;
    }
    const dragType = e.active.data?.current?.type;
    const oldIdx = e.active?.data?.current?.index;
    const newIdx = e.over?.data?.current?.index;

    if (oldIdx === undefined || newIdx === undefined) {
      return;
    }

    if (dragType === DragType.ROW) {
      dispatch(
        reorderTableRows({
          threadId: currentThread.id,
          rows: reorderItems(rows, oldIdx, newIdx),
        }),
      );
    } else if (dragType === DragType.COLUMN) {
      dispatch(
        reorderTableColumns({
          threadId: currentThread.id,
          columns: reorderItems(columns, oldIdx, newIdx),
        }),
      );
    } else {
      console.error('incorrect drag type');
    }
  };

  const reorderItems = <T,>(
    items: T[],
    oldIdx: number,
    newIdx: number,
  ): T[] => {
    const reorderedItems = [...items];
    const [removed] = reorderedItems.splice(oldIdx, 1);
    reorderedItems.splice(newIdx, 0, removed);
    return reorderedItems;
  };

  return (
    <>
      <TableCard>
        <TableCardContent>
          <TableHeader>
            {!!title && <div>{title}</div>}
            <CloseButton
              onClick={closePanel}
              disabled={!currentThread}
              size="small"
              {...mkCloseBtnTrackAttrs()}
            >
              <HighlightOffIcon />
            </CloseButton>
          </TableHeader>
          {/* TODO: move to separate component once there are more actions */}
          {noraExperienceVariant !== TABLE_ANNOTATION &&
            numRowsSelected > 0 && (
              <FilterBar>
                <Button
                  variant="outlined"
                  onClick={removeSelectedRows}
                  {...mkHeaderDeleteSelectedBtnTrackAttrs({
                    selectedRowCount: numRowsSelected,
                  })}
                >
                  Remove Selected Rows
                </Button>
              </FilterBar>
            )}
          {tableData?.isLoading && <LoadingTable />}
          {tableData && !tableData.isLoading && (
            <Fragment>
              <TableView
                rows={rows}
                columns={columns}
                cells={cells}
                numRowSelected={numRowsSelected}
                handleSelectAllRows={handleSelectAllRows}
                handleChangeRowSelection={handleChangeRowSelection}
                handleClickAddColumn={openAddColumnModal}
                handleEditCell={handleEditTableCell}
                handleReorderTable={onDragEnd}
                mode={TableViewMode.LIT_REVIEW}
              />
              {!!currentThread && isAddColumnModalOpen && (
                <ColumnModal
                  threadId={currentThread.id}
                  onClose={closeAddColumnModal}
                />
              )}
            </Fragment>
          )}
        </TableCardContent>
      </TableCard>
      {!isReadOnly && noraExperienceVariant !== TABLE_ANNOTATION && (
        <TableCard
          id="table-suggestions"
          sx={{
            flexGrow: 0,
            color: 'red',
            maxHeight: '48vh',
            overflow: 'visible',
          }}
        >
          <TableCardContent>
            <TableControls />
          </TableCardContent>
        </TableCard>
      )}
      {isTableEmpty && !tableData?.isLoading && (
        <StyledTableRow>
          <Typography variant="subtitle2" color="lightgray">
            Ask Nora to add papers and suggested common aspects to see a
            literature comparison table. For example, &quot;
            <a href="#" onClick={handleShowSuggestion}>
              {suggestedQuery}
            </a>
            &quot;
          </Typography>
        </StyledTableRow>
      )}
      {optAppConfig((_) => _.table.is_remote_visible) ? <TableRemote /> : null}
    </>
  );
};

const TableCard = styled(Card)`
  background: ${({ theme }) => theme.color['dark-teal-100'].hex};
  color: ${({ theme }) => theme.color['cream-100'].hex};
  flex-grow: 1;
`;

const TableCardContent = styled(CardContent)`
  background-color: inherit;
  height: 100%;
  padding: 0;
  :last-child {
    padding-bottom: 0;
  }
`;

const FilterBar = styled(Box)`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  min-height: 50px;
  padding: ${({ theme }) => theme.spacing(1)};
  width: 100%;
`;

const CloseButton = styled(IconButton)`
  color: ${({ theme }) => theme.color['pink-100'].hex};
  position: absolute;
  right: ${({ theme }) => theme.spacing(2)};
  top: ${({ theme }) => theme.spacing(1)};
  z-index: 5;
`;

const TableHeader = styled('div')`
  font-size: 20px;
  padding: ${({ theme }) => theme.spacing(2.25, 3)};
  border-bottom: 1px solid rgba(250, 242, 243, 0.1);
  justify-content: space-between;
  display: flex;
  align-items: center;
`;
